import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { capturarOrigem } from '@titularis/analytics';
import { TitularisProvider } from '@titularis/ui';
import 'antd/dist/reset.css';
import { App } from './App';
import { iniciarAnalytics } from './analytics';
import { guardarTokenDeRedefinicao } from './pages/auth/tokenDeRedefinicao';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      // 'always' impede o React Query de pausar a query silenciosamente quando
      // considera o navegador offline: preferimos tentar e mostrar o erro.
      networkMode: 'always',
    },
  },
});

/*
 * Antes do PostHog, e a ordem é o ponto: o autocapture e a gravação leem o
 * endereço desde o primeiro instante, e o token de `/nova-senha` precisa já ter
 * saído dele.
 */
guardarTokenDeRedefinicao();

// Sem chave configurada, não inicia nada: o app funciona igual.
iniciarAnalytics();

/*
 * **O app também captura a origem, e não só a lê.**
 *
 * Até aqui a captura morava só no `apps/site`, e o app apenas consultava o
 * cookie que a landing tinha deixado. Isso funciona para quem entra pela porta
 * da frente e **perde o canal inteiro** para quem não entra: o link que se
 * posta num grupo aponta direto para o cadastro, porque encurtar o caminho é o
 * ponto. `app.titularis.com.br/criar-conta?utm_source=whatsapp&utm_campaign=x`
 * chegava sem atribuição nenhuma, e a conta nascia como tráfego direto.
 *
 * Roda antes do React porque a atribuição é do documento, não da tela: a
 * `RequireAuth` lê o cookie assim que há sessão, e com a rota já montada a
 * leitura poderia acontecer antes da escrita.
 *
 * A guarda de auto-indicação vive no pacote, e é o que impede esta linha de
 * transformar toda travessia da landing para cá num canal chamado "titularis".
 */
capturarOrigem();

const container = document.getElementById('root');
if (!container) throw new Error('Elemento #root não encontrado.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TitularisProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TitularisProvider>
    </QueryClientProvider>
  </StrictMode>,
);
